import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../singleton/axios';
import { useCallback } from 'react';

export interface TourStatusResponse {
  data: {
    isCompleted: boolean;
  };
}

export interface UpdateTourStatusResponse {
  data: {
    isCompleted: boolean;
    updated: boolean;
  };
}

// Hook to get user's tour completion status
export const useTourStatus = () => {
  // Check if we're on an auth page where we don't need tour status
  const isAuthPage = typeof window !== 'undefined' && (
    window.location.pathname === '/sign-in' ||
    window.location.pathname === '/sign-up' ||
    window.location.pathname === '/forgot-password' ||
    window.location.pathname === '/reset-password' ||
    window.location.pathname === '/verify-email' ||
    window.location.pathname === '/terms' ||
    window.location.pathname === '/privacy-policy' ||
    window.location.pathname.startsWith('/auth/')
  );

  return useQuery({
    queryKey: ['userTourStatus'],
    queryFn: async () => {
      const response = await api.get<TourStatusResponse>('/user-settings/tour');
      return response.data;
    },
    staleTime: Infinity, // Tour status rarely changes
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: false,
    enabled: !isAuthPage,
  });
};

// Hook to update user's tour completion status
export const useUpdateTourStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (isCompleted: boolean) => {
      const response = await api.put<UpdateTourStatusResponse>('/user-settings/tour', { isCompleted });
      return response.data;
    },
    onSuccess: (data) => {
      // Update the cache with the new tour status
      queryClient.setQueryData(['userTourStatus'], (oldData: TourStatusResponse | undefined) => {
        if (!oldData) return { data: { isCompleted: data.data.isCompleted } };
        return {
          ...oldData,
          data: {
            ...oldData.data,
            isCompleted: data.data.isCompleted,
          },
        };
      });
    },
  });
};

// Combined hook for tour settings management
export const useTourSettings = () => {
  const {
    data: tourData,
    isLoading,
    error: queryError,
    refetch,
  } = useTourStatus();

  const updateMutation = useUpdateTourStatus();

  const completeTour = useCallback(async () => {
    try {
      await updateMutation.mutateAsync(true);
    } catch (err) {
      console.error('Failed to mark tour as completed:', err);
      throw err;
    }
  }, [updateMutation]);

  const resetTour = useCallback(async () => {
    try {
      await updateMutation.mutateAsync(false);
    } catch (err) {
      console.error('Failed to reset tour:', err);
      throw err;
    }
  }, [updateMutation]);

  return {
    isCompleted: tourData?.data.isCompleted ?? false,
    isLoading,
    isUpdating: updateMutation.isPending,
    error: queryError ?? updateMutation.error,
    completeTour,
    resetTour,
    refetch,
  };
};
